import * as React from 'react';
import { Fragment } from 'react';
import RAP, { AdditionalDetails } from './RAP';
import Amount from './Amount';
import '../css/breakdown-rap.sass';
import { ActionButton } from './ActionButtons';
import CampfireButton from './CampfireButton';
import PinnedContent from './PinnedContent';
import { BreakDownHeader, BreakDownPriceSectionType } from './Breakdown';
import formatter from '../utils/formatter-util';

export interface BreakdownRAPProps {
  title?: string;
  // Either details or sections must be passed
  details?: BreakDownHeader[];
  sections?: BreakDownPriceSectionType[];
  total: number;
  totalLabel?: string;
  theme?: 'night' | 'day';
  elementRefToPositionRAP?: string;
  button?: ActionButton;
  additionalDetails?: AdditionalDetails;
  rapButtonLabel?: string;
  scrollRAPStopId?: string;
  scrollRAPTriggerId?: string;
}

interface BreakdownRAPState {
  toggleRAP: boolean;
}
export default function BreakdownRAP(props: BreakdownRAPProps) {
  const [state, setState] = React.useState<BreakdownRAPState>({
    toggleRAP: false
  });
  const {
    theme = 'day',
    elementRefToPositionRAP = 'breakdownRAP',
    rapButtonLabel = 'Review',
    totalLabel = 'Monthly Total'
  } = props;

  const rapButton = async () => {
    if (props.button && props.button.onClick) {
      if (state.toggleRAP) {
        await props.button.onClick();
        setState({ ...state, toggleRAP: false });
      } else {
        props.button.onClick();
      }
    }
  };

  return (
    <>
      <div className={`breakdown-rap ${theme}`} id="breakdownRAP">
        {!state.toggleRAP && (
          <PinnedContent
            theme={theme}
            value={formatter({
              type: 'number',
              format: 'amount',
              value: props.total.toString()
            })}
            label={totalLabel}
            rapButtonLabel={rapButtonLabel}
            onRAPBtnClick={() => {
              setState({ ...state, toggleRAP: true });
            }}
          />
        )}
        <RAP
          theme={theme}
          elementRefToPositionRAP={elementRefToPositionRAP}
          additionalDetails={props.additionalDetails}
          toggleRAP={state.toggleRAP}
          onRAPPopupClose={() => {
            setState({ ...state, toggleRAP: false });
          }}
          pinnedPosition="LOAD"
          fixPositionOnScroll
        >
          <div className="breakdown-rap-contents">
            <div className="title">{props.title || 'Proposed Changes'}</div>
            <div className="sub-heading">
              {props.additionalDetails && (
                <p>
                  {props.additionalDetails.label}&nbsp;
                  {props.additionalDetails.description}
                </p>
              )}
            </div>
            {props.details &&
              props.details.map(detail => {
                return (
                  <Fragment key={detail.name}>
                    <BreakdownItem name={detail.name} amount={detail.amount} />
                  </Fragment>
                );
              })}
            {props.sections &&
              props.sections.map((section, index) => {
                const isLastItem =
                  props.sections && index === props.sections.length - 1;
                return (
                  <div
                    key={section.header.name}
                    className={isLastItem ? undefined : 'sub-section'}
                  >
                    {section.details?.map(detail => (
                      <Fragment key={detail.name}>
                        <BreakdownItem
                          name={detail.name}
                          amount={detail.amount.toString()}
                        />
                      </Fragment>
                    ))}
                    {!isLastItem && <div className="sub-divider"></div>}
                    <BreakdownItem
                      name={section.header.name}
                      amount={section.header.amount}
                    />
                  </div>
                );
              })}
            <div className="divider"></div>
            <div className="totalLabel">
              <span>{totalLabel}</span>
              <div className="amount bold">
                <Amount amount={props.total} />
              </div>
            </div>
            {props.button && (
              <div className="alignBtn">
                <CampfireButton
                  theme={theme}
                  name="breakdownRAPBtn"
                  id="breakdownRAPBtn"
                  label={props.button.label}
                  color="water"
                  disabled={props.button.disabled}
                  smol
                  onClick={rapButton}
                  sendTo={props.button.sendTo}
                  frosty={
                    !props.button.type || props.button.type === 'secondary'
                  }
                  ghost={
                    !props.button.type || props.button.type === 'secondary'
                  }
                />
              </div>
            )}
          </div>
        </RAP>
      </div>
    </>
  );
}

function BreakdownItem(props: { name: string; amount: string }) {
  return (
    <div className="br-details">
      <span>{props.name}</span>
      <div className="amount bold">
        <Amount amount={props.amount} />
      </div>
    </div>
  );
}
